import { useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { toast } from 'react-toastify'
import { useMutation, useQueryClient } from 'react-query'
import { useNavigate } from 'react-router-dom'
import { Box, Stack, Tooltip, Typography, useMediaQuery } from '@mui/material'
import { Add } from '@mui/icons-material'
import { useTheme } from '@mui/material/styles'
import { successToast } from 'utils/toastService'
import {
  BinSuggestionsCardContainer,
  Body,
  Container,
  Header,
  HeaderSectionTitle,
} from './BinSuggestions.styled'
import { BinSuggestionItem } from './BinSuggestionItem'
import useAcceptStorageSolutionModal from '../../dialogs/useAcceptStorageSolutionModal'
import { Button, CardTitle } from '../../../../../../components'
import { SuggestedBins } from '../../../../../../types'
import useBinningsService from '../../../../../../services/binningsService'
import { RootState } from '../../../../../../store'
import {
  CrudAction,
  Entity,
  usePermissions,
} from '../../../../../../utils/hooks'

export const displayedBinAttributes = [
  'Protein',
  'Wet Gluten',
  'Falling Number',
]

type BinSuggestionTypes = {
  discarded?: boolean
  suggestedBins: SuggestedBins[]
}

export const BinSuggestions = ({
  discarded,
  suggestedBins,
}: BinSuggestionTypes) => {
  const { checkPermission } = usePermissions()
  const queryClient = useQueryClient()
  const { setBinAccepted } = useBinningsService()

  const navigate = useNavigate()
  const theme = useTheme()
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'))
  const isMediumScreen = useMediaQuery(theme.breakpoints.down('lg'))
  const isLargeScreen = useMediaQuery(theme.breakpoints.down('xl'))
  const { isSideNavOpen } = useSelector((state: RootState) => state.sideNav)

  const [selectedBinId, setSelectedBinId] = useState<number>(null)

  const initialBinId = useMemo(() => {
    if (!suggestedBins || suggestedBins.length === 0) return null
    return (
      suggestedBins.find((s) => s.is_target)?.bin?.id ??
      suggestedBins.find((s) => s.bin_accepted)?.bin?.id ??
      suggestedBins[0]?.bin?.id
    )
  }, [suggestedBins])

  const acceptedBinId = useMemo(() => {
    if (!suggestedBins || suggestedBins.length === 0) return null
    return suggestedBins.find((s) => s.bin_accepted)?.id ?? null
  }, [suggestedBins])

  useEffect(() => {
    const binIds = suggestedBins?.map((s) => s.bin?.id) || []
    if (selectedBinId && !binIds.includes(selectedBinId)) {
      setSelectedBinId(initialBinId)
    }
  }, [suggestedBins, selectedBinId, initialBinId])

  useEffect(() => {
    if (!selectedBinId && initialBinId) {
      setSelectedBinId(initialBinId)
    }
  }, [initialBinId, selectedBinId])

  const handleCancelStorageSolution = () => {
    navigate('/shipments')
  }

  const [acceptStorageSolutionModal, toggleAcceptStorageSolutionModal] =
    useAcceptStorageSolutionModal({
      onConfirm() {
        handleAcceptStorageSolution()
      },
    })

  const setBinAcceptedMutation = useMutation(
    (binningId: number) => setBinAccepted(binningId),
    {
      onSuccess: (data) => {
        if (data?.status === true) {
          void queryClient.invalidateQueries(['delivery_by_id'])
          successToast('Storage solution accepted')
          navigate('/shipments')
        }
      },
      onError: (error: any) => {
        toast.error(error?.detail || 'Error while accepting solution')
      },
    }
  )

  const handleAcceptStorageSolution = () => {
    if (!selectedBinId) {
      toast.error('Bin must be selected')
    }
    setBinAcceptedMutation.mutate(
      suggestedBins.find((s) => s.bin.id === selectedBinId)?.id
    )
  }

  const renderAttributes =
    !isSmallScreen && !(isSideNavOpen && isLargeScreen && !isMediumScreen)

  return (
    <BinSuggestionsCardContainer>
      <Box flex={1} px="16px" pb="16px" flexGrow={0}>
        <CardTitle title="Storage Suggestions" />
      </Box>
      <Container>
        <Header>
          <HeaderSectionTitle
            sx={{
              width: 150,
            }}
          >
            <Typography>Equinom Bin</Typography>
          </HeaderSectionTitle>
          {renderAttributes &&
            Array.isArray(suggestedBins?.[0]?.bin?.attributes) &&
            suggestedBins?.[0]?.bin?.attributes
              .filter((attribute) =>
                displayedBinAttributes.includes(attribute.name)
              )
              .map((attribute, index) => {
                return (
                  <HeaderSectionTitle
                    sx={{
                      width: index === 2 ? 120 : 100,
                    }}
                    key={attribute?.name}
                  >
                    <Typography noWrap>{attribute.name}</Typography>
                  </HeaderSectionTitle>
                )
              })}
          <HeaderSectionTitle sx={{ flex: 1 }}>
            <Typography>Compatibility</Typography>
          </HeaderSectionTitle>
        </Header>
        <Body>
          {suggestedBins?.map((suggestionItem) => {
            return (
              <BinSuggestionItem
                key={suggestionItem.id}
                suggestedBin={suggestionItem}
                isSelected={suggestionItem.bin.id === selectedBinId}
                disabled={!!acceptedBinId}
                onClick={() => setSelectedBinId(suggestionItem.bin.id)}
              />
            )
          })}
        </Body>
        <Stack
          paddingY="16px"
          flexDirection="row"
          justifyContent="space-between"
          alignItems="flex-end"
        >
          <Button onClick={handleCancelStorageSolution} variantType="secondary">
            Cancel
          </Button>
          <Stack direction="row" alignItems="center">
            <Tooltip
              title={
                acceptedBinId
                  ? 'Bin is already accepted'
                  : discarded && 'Bin is discarded'
              }
            >
              <div>
                <Button
                  disabled={
                    acceptedBinId
                      ? true
                      : discarded ||
                        !checkPermission(Entity.DELIVERY, CrudAction.UPDATE)
                  }
                  onClick={toggleAcceptStorageSolutionModal}
                  startIcon={<Add />}
                >
                  {isSmallScreen ? 'Accept' : 'Accept Storage Solution'}
                </Button>
              </div>
            </Tooltip>
          </Stack>
        </Stack>
      </Container>
      {acceptStorageSolutionModal}
    </BinSuggestionsCardContainer>
  )
}
